import { LabelValue } from '@/types/global';
import useTableControls from '@/hooks/useTableControls';
import { Control, useForm, useWatch } from 'react-hook-form';
import { Dispatch, SetStateAction, useState } from 'react';
import { useGetAllReceivers } from '@/hooks/useReceivers';
import { useDebouncedSearch } from '@/hooks/useDebouncedSearch';
import Loader from '@/components/ui/Loader';
import StyledSelect from '@/components/styled/StyledSelect';
import StyledInput from '@/components/styled/StyledInput';
import StyledTable from '@/components/styled/StyledTable';
import {
  RECEIVER_TO_ADD_COLUMNS,
  ReceiverToAdd,
} from '@/types/receivers/ReceiverToAddColumnDef';
import StyledNextPreviousButtons from '@/components/styled/StyledNextPreviousButtons';
import { PAGE_SIZE } from '@/constants/pageSize';
import SearchIcon from '@/assets/icons/search.svg?react';
import { ReceiversToAddResponse } from '@/api/receiver.api';
import { Row, RowSelectionState, SortingState } from '@tanstack/react-table';
import ReceiverInfoModal from '@/components/receivers/ReceiverInfoModal';
import { useTranslation } from '@/i18n';
import { useTranslatedConst } from '@/hooks/useTranslatedConst';

interface ReceiverFilters {
  search: string;
  country?: LabelValue<string>;
}

interface AddReceiversPageProps {
  onlyRecent: boolean;
  setSelection: Dispatch<SetStateAction<RowSelectionState>>;
  setReceiversToSendPartnershipRequest: Dispatch<SetStateAction<string[]>>;
}

const AddReceiversPage = ({
  onlyRecent,
  setSelection,
  setReceiversToSendPartnershipRequest,
}: AddReceiversPageProps) => {
  const { control } = useForm<ReceiverFilters>({
    mode: 'onChange',
    defaultValues: {
      search: '',
      country: undefined,
    },
  });

  const searchValue = useWatch({ control, name: 'search' });
  const debouncedSearch = useDebouncedSearch(searchValue);
  const countryValue = useWatch({ control, name: 'country' });

  const filters = {
    name: debouncedSearch,
    country: countryValue?.value ?? '',
  };

  const { page, setPage, setSorting, sorting, sortField, sortOrder } =
    useTableControls();

  const {
    data: receiversListResponse,
    isSuccess,
    isLoading,
  } = useGetAllReceivers(
    {
      page,
      pageSize: PAGE_SIZE,
      sortField,
      sortOrder,
      filters,
      onlyRecent,
    },
    (data) => ({
      ...data,
      countries: data.countries.map((country) => ({
        label: country,
        value: country,
      })),
    }),
  );

  return isLoading ? (
    <Loader />
  ) : (
    isSuccess && (
      <ReceiversList
        receiversListResponse={receiversListResponse}
        page={page}
        setPage={setPage}
        sorting={sorting}
        setSorting={setSorting}
        control={control}
        setSelection={setSelection}
        onAddRow={(row) => {
          setReceiversToSendPartnershipRequest([row.original.id]);
        }}
      />
    )
  );
};

export default AddReceiversPage;

const ReceiversList = ({
  receiversListResponse,
  page,
  setPage,
  sorting,
  setSorting,
  control,
  setSelection,
  onAddRow,
}: {
  receiversListResponse: Omit<ReceiversToAddResponse, 'countries'> & {
    countries: LabelValue<string>[];
  };
  page: number;
  setPage: Dispatch<SetStateAction<number>>;
  sorting: SortingState;
  setSorting: Dispatch<SetStateAction<SortingState>>;
  control: Control<ReceiverFilters>;
  setSelection: Dispatch<SetStateAction<RowSelectionState>>;
  onAddRow: (row: Row<ReceiverToAdd>) => void;
}) => {
  const { t } = useTranslation();
  const getTranslatedConst = useTranslatedConst();
  const [receiverToViewId, setReceiverToViewId] = useState<string | null>(null);

  return (
    <div className="flex flex-col gap-6 w-full h-auto pt-4">
      <div className="flex flex-col gap-2">
        <span className="text-xs font-normal text-gray-600">
          {t('receivers.filterBy')}
        </span>
        <div className="flex flex-col md:flex-row justify-between gap-4">
          <div className="flex flex-col md:flex-row gap-4">
            <StyledSelect
              className="min-w-1/3 md:min-w-48 lg:min-w-60 xl:min-w-80"
              control={control}
              options={receiversListResponse.countries}
              name="country"
              placeholder={t('pages.receivers.country')}
              isClearable={true}
            />
          </div>
          <div className="w-full md:w-1/3 lg:w-1/4 2xl:w-1/5">
            <StyledInput
              control={control}
              name="search"
              placeholder={t('pages.receivers.quickSearch')}
              icon={<SearchIcon className="w-5 h-5 stroke-gray-500" />}
            />
          </div>
        </div>
      </div>
      <StyledTable
        columns={getTranslatedConst(RECEIVER_TO_ADD_COLUMNS)}
        data={receiversListResponse.allReceivers}
        sorting={sorting}
        setSorting={setSorting}
        getRowId={(row) => row.id}
        setSelection={setSelection}
        onAddRow={onAddRow}
        onViewRow={(row) => {
          setReceiverToViewId(row.original.id);
        }}
      />
      <StyledNextPreviousButtons
        page={page}
        pageSize={PAGE_SIZE}
        setPage={setPage}
        dataLength={receiversListResponse.total}
        label="fournisseur"
      />
      {receiverToViewId && (
        <ReceiverInfoModal
          receiverId={receiverToViewId}
          closeModal={() => setReceiverToViewId(null)}
        />
      )}
    </div>
  );
};
